import { call, put, all, takeEvery } from "redux-saga/effects";
import { authActions, generalActions } from "../actions";
import { api as authService } from "../services/auth.service";
import { mfaService } from "../services/mfa.service";
import generalApi from "../services/general.service";
import ACTIONS from "../constants";
import { jwtDecode } from "jwt-decode";
import { clearMfaTempData, removeCookie, setEncryptedCookie, setEncryptedMfaTempData } from "../../utils/cookieUtils";
import { setLocalStorage, keys as localStorageKeys, clearLocalStorage } from '../../utils/localStorageUtils';
import { RoleType } from "../../helpers/utils";
import { resetLoginActionsAttempt } from "../actions/auth.actions";
import { clearScheduledRefresh, scheduleTokenRefresh } from "../../helpers/tokenManager";

function* fetchLogo(state) {
  try {
    yield put(authActions.fetchLogoAttempt.pending);
    const response = yield call(authService.logoRequest, state?.payload?.domain);
    localStorage.setItem("logo", response.url);
    yield put(authActions.fetchLogoAttempt.success(response));
  } catch (error) {
    yield put(authActions.fetchLogoAttempt.error(error.message));
  }
}

function* login(state) {
  const isVerifyMfa = state?.payload?.isVerifyMfa || false;

  const payload = isVerifyMfa ?
    {
      tempToken: state?.payload?.tempToken,
      code: state?.payload?.code,
    } :
    {
      login: state?.payload?.Login,
      password: state?.payload?.password,
    };

  try {

    yield put(authActions.loginAttempt.pending);

    const authResponse = isVerifyMfa ?
      yield call(
        mfaService.verifyLogin,
        payload,
        state?.payload?.domain
      )
      :
      yield call(
        authService.loginRequest,
        payload,
        state?.payload?.domain
      );


    if (authResponse?.success) {
      if (!isVerifyMfa) {
        if (authResponse?.data?.isMfaRequired) {
          setEncryptedMfaTempData({ token: authResponse?.data?.tempToken, domain: state?.payload?.domain });
          yield put(authActions.loginAttempt.success({ isMfaRequired: true }));
        }
        else if (authResponse?.data?.isMfaSetupRequired) {
          yield put(authActions.loginAttempt.success({ isMfaSetupRequired: true, token: authResponse?.data?.accessToken }));
        }
        else if (authResponse?.data?.isPasswordResetRequired) {
          yield put(authActions.loginAttempt.success({ isPasswordResetRequired: true, token: authResponse?.data?.token }));
        }
        else{
          yield handleConnectedUser(state, authResponse, isVerifyMfa);
        }
      }
      else {
        yield handleConnectedUser(state, authResponse, isVerifyMfa);
      }

    }
    else {
      yield put(authActions.loginAttempt.error({ error: authResponse?.error || "Login failed", code: authResponse?.code }, isVerifyMfa));
      return;
    }

  } catch (error) {

    if (error.status === 500) {
      yield put(authActions.loginAttempt.error({ error: "", code: error.status }, isVerifyMfa));
      yield put(
        generalActions.showToast({
          title: "Something went wrong. Please try again later.",
          type: "error",
        })
      );
      return;
    }

    const errorMessage = error?.data?.error || error?.data?.Message || "Login failed";
    yield put(authActions.loginAttempt.error({ error: errorMessage, code: error.status }, isVerifyMfa));
    return;
  }
}
function* forgotPassword(state) {
  try {
    yield put(authActions.forgotPasswordAttempt.pending);
    const response = yield call(authService.forgotPasswordRequest, state.payload);
    yield put(authActions.forgotPasswordAttempt.success(response));
  } catch (error) {
    yield put(authActions.forgotPasswordAttempt.error(error.message));
  }
}

function* resetPassword(state) {
  try {
    yield put(authActions.resetPasswordAttempt.pending);
    yield call(authService.resetPasswordRequest, state.payload);
    yield put(authActions.resetPasswordAttempt.success({ isSuccess: true, message: "Password changed successfully" }));
  } catch (error) {
    yield put(authActions.resetPasswordAttempt.error({ isSuccess: true, message: error.data.message || "Failed to reset password" }));
  }
}

function* logout() {
  yield clearScheduledRefresh();
  yield clearLocalStorage();
  yield put(authActions.logoutSucceed());
}

function* resetLoginActions() {
  yield put(authActions.resetLoginActionsAttempt.success());
}

function* handleConnectedUser (state, authResponse, isVerifyMfa) {
  var response = authResponse?.data;
  yield localStorage.setItem("accessToken", JSON.stringify(response.accessToken));
  yield localStorage.setItem("refreshToken", JSON.stringify(response.refreshToken));
  yield scheduleTokenRefresh();
  const decoded = jwtDecode(response.accessToken);
  const id = decoded.id;
  yield localStorage.setItem("id", id);
  const user = yield call(authService.selfDataRequest);
  const permissions = yield call(authService.selfPermissionsRequest);

  if (user) {
    yield localStorage.setItem("user", JSON.stringify(user));
    yield localStorage.setItem("roles", JSON.stringify(user.roles));
    yield setLocalStorage(localStorageKeys.HAS_DEFAULT_ADMIN_ROLE, !!user.roles.find(r => r.type == RoleType.Administrator && r.isDefault));
    yield localStorage.setItem("user", JSON.stringify(user));
  }

  if (permissions)
    yield localStorage.setItem("permissions", JSON.stringify(permissions.permissions));

  response.user = user;
  const settings = yield call(generalApi.settingsRequest);
  if (settings)
    yield localStorage.setItem("settings", JSON.stringify(settings));

  yield put(authActions.loginAttempt.success(response));

  if (state?.payload?.rememberMe && state?.payload?.checked) {
    setEncryptedCookie("email", state?.payload?.Login);
    setEncryptedCookie("password", state?.payload?.password);
  } else {
    removeCookie("email");
    removeCookie("password");
  }

  if (isVerifyMfa)
    clearMfaTempData();
};

export default function* rootSaga() {
  yield all([
    takeEvery(ACTIONS.FETCH_LOGO_BEGIN, fetchLogo),
    takeEvery(ACTIONS.LOGIN_CALL, login),
    takeEvery(ACTIONS.FORGOT_PASSWORD_CALL, forgotPassword),
    takeEvery(ACTIONS.RESET_PASSWORD_CALL, resetPassword),
    takeEvery(ACTIONS.AUTH_LOGOUT_BEGIN, logout),
    takeEvery(ACTIONS.RESET_LOGIN_ACTIONS_CALL, resetLoginActions),
  ]);
}
